import { useLanguage } from '../core/hooks/use-language';
import { usePageMetadata } from '../core/hooks/use-page-metadata';
import { STATIC_PAGES, staticPageMetadata } from '../core/seo/static-pages';
import { AboutSection } from './home/about-section';
import { ContactSection } from './home/contact-section';
import { FaqSection } from './home/faq-section';
import { HeroSection } from './home/hero-section';
import { HomeFooter } from './home/home-footer';
import { PartnersSection } from './home/partners-section';
import { ProgramsSection } from './home/programs-section';
import { SocialsSection } from './home/socials-section';
import { SpecialistsSection } from './home/specialists-section';

/**
 * The public landing page, ported from the live static site in `WebClient/index.html`.
 *
 * **This is a port, not a redesign.** The page renders the same class names the static page does
 * and is styled by the same stylesheet, carried over unchanged into `src/styles/legacy/`; what
 * changed is that the markup is now components and the words come from the typed string tables.
 * Anything that would alter the page's appearance belongs in that stylesheet and in a decision to
 * change the site, not here.
 *
 * **Two failure classes are fixed by the move itself.** The live engine does
 * `el.textContent = value ?? original`, so a key added to the Kazakh markup with no Russian entry
 * renders Kazakh to a Russian reader, silently — here both tables are typed against the Kazakh set,
 * so the same mistake is a compile error. And the FAQ question and the burger are buttons rather
 * than `<div onclick>`, so the page can be operated from a keyboard.
 *
 * **It brings its own header and footer**, which is why it is routed outside `SiteChrome`: the
 * navigation on this page sits inside the hero, transparent over the gradient, and the footer is
 * the site's dark one. Wrapping it in the shared chrome would put a white bar above the gradient
 * and a second footer below the first.
 */
export function HomePage() {
    const language = useLanguage();

    // The same description the build wrote into this page's served HTML, from the same list.
    usePageMetadata(staticPageMetadata(STATIC_PAGES[0], language));

    return (
        <>
            <HeroSection />
            <AboutSection />
            <SpecialistsSection />
            <ProgramsSection />
            <FaqSection />
            <SocialsSection />
            <PartnersSection />
            <ContactSection />
            <HomeFooter />
        </>
    );
}
